import { createContext, useContext } from 'react';
import { isPracticeActive, isPracticeId } from './practiceGuards';

/**
 * "This editor instance belongs to the guided tour."
 *
 * The cost-price editor can be on screen several times over — the user's own drawer, the
 * picker inside it, a create-subrecipe form, the picker nested inside THAT — and the tour
 * drives its steps with window events, which reach every one of them. Without a scope, a
 * `cost-confirm-add` meant for the practice dish would also fire the real drawer's save.
 *
 * So the practice drawer publishes this flag and everything below it reads it. Two things
 * hang off it, and both have to: the `onboarding:cost-*` listeners, so no real editor ever
 * acts on a tour event, and the `data-onboarding` anchors, so the resolver's layer
 * detection can never mistake a real panel for the practice one.
 *
 * Context, not a module flag, because the answer differs per instance: a session is active
 * for all of them at once, and only one of them is the tour's.
 */
const PracticeScopeContext = createContext(false);

export const PracticeScopeProvider = PracticeScopeContext.Provider;

/** True only inside the tour's own editor. Everything else in the tree gets false. */
export function usePracticeScope(): boolean {
  return useContext(PracticeScopeContext);
}

/** The root test, for the drawer that opens the scope. A practice id implies a session. */
export function isPracticeScopeRoot(costPriceId: string | null | undefined): boolean {
  return isPracticeActive() && isPracticeId(costPriceId);
}

/** Convenience for anchors: the id when in scope, `undefined` otherwise. */
export function scopedAnchor(inScope: boolean, id: string): string | undefined {
  return inScope ? id : undefined;
}
